import { useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getUploadStatus } from "@services/API/Requests";
import { postUserDataArrayCSV } from "@services/API/Requests/UserRequests";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { USER } from "@types/user";
import { GetCurrentDate, removeHyphens } from "@services/Utilities";
import { error_message } from "@constants/Errors";
import messages from "@constants/Messages";
import { POLLING_MESSAGES, USER_TYPE } from "@constants/Enums";
import { USER_PATHS } from "@constants/NavigationPaths";
import { METHOD_TYPE } from "@constants/Enums";

export const UseUserCsvUploadPage = () => {
  const navigate: NavigateFunction = useNavigate();

  const [csvData, setCsvData] = useState<[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [failedUsers, setFailedUsers] = useState<[]>();

  const csvNotFoundErrorMessage = "Couldn't find User with 'id'";
  const deleteCsvNotFoundMessage = "csv:delete_id_not_found";
  const updateCsvNotFoundMessage = "csv:update_id_not_found";

  const currentDate = GetCurrentDate();

  const noCsvImportError = () => toast.error(error_message.csv.no_data);
  const uploadCsvDataToast = (message) => toast.success(message);
  const failCsvDataToast = (error) => toast.error(`${error}`);

  const [finalResult, setFinalResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);

  const handleCsvUpload = async () => {
    setFinalResult(null);
    setLoading(true);
    if (csvData.length === 0) {
      noCsvImportError();
      return;
    }

    const userDataArray = csvData.map((userInfo: USER, index: number) => {
      const isDisplay = String(userInfo.is_display).toLowerCase() === "true";

      const userType: USER_TYPE =
        userInfo.role != "3" ? USER_TYPE.KEYCLOAK_USER : USER_TYPE.INTERNAL;

      const user: USER = {
        id: userInfo.id,
        employee_number: userInfo.employee_number,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        first_kana_name: userInfo.first_kana_name,
        last_kana_name: userInfo.last_kana_name,
        first_furigana_name: userInfo.first_furigana_name,
        last_furigana_name: userInfo.last_furigana_name,
        user_type: userType,
        personal_tel: removeHyphens(userInfo.personal_tel),
        secondary_phone_number: removeHyphens(userInfo.secondary_phone_number),
        email: userInfo.email,
        secondary_email: userInfo.secondary_email,
        is_display: isDisplay,
        login_id: userInfo.login_id,
        login_password: userInfo.login_password,
        display_order: userInfo.display_order,
        position_name: userInfo.position_name,
        broadcast_group_code: userInfo.broadcast_group_code,
        delete: userInfo.delete,
        created_at: userInfo.created_at,
        department_name: userInfo.department_name,
        company_name: userInfo.company_name,
        company_address: userInfo.company_address,
        company_postal_code: userInfo.company_postal_code,
        company_fax: userInfo.company_fax,
        company_tel: userInfo.company_tel,
        company_home_page: userInfo.company_home_page,
        notes: userInfo.notes,
      };

      const roleData = {
        id: userInfo.role || import.meta.env.VITE_ROLE_ID,
      };

      const userTenantData = {
        tenant_department_id: userInfo.tenant_department_id || 0,
      };

      const appsArray = userInfo.apps?.split(",");

      let userApps = [];
      if (appsArray && appsArray[0] !== "") {
        userApps = appsArray.map((appName) => {
          return { name: appName };
        });
      }

      return { userInfo: user, userTenantData, roleData, userApps, index };
    });

    const postUserInformation = async (userDataArray) => {
      let response;

      setCsvData((prevCsvData) =>
        prevCsvData.map((user, newIndex) => ({ ...user, index: newIndex })),
      );

      try {
        response = await postUserDataArrayCSV(
          userDataArray,
          setLoading,
          setError,
        );
        if (
          response.responseMessage.status >= 300 ||
          response.responseMessage.status == null
        ) {
          const errorMessage = TranslateErrorMessage(response.responseMessage);
          setFailedUsers(response.failed_users);
          console.error(errorMessage);
          setError(errorMessage);
          failCsvDataToast(response.failed_users.error);
          return;
        } else if (response.responseMessage.status == 202) {
          response = await pollForStatus(
            response.request_id,
            userDataArray.length,
            2000,
            5000,
          );
        } else {
          uploadCsvDataToast(
            messages.user.uploaded(
              response.created_users.length || 1,
              response.failed_users.length || 0,
            ),
          );
        }
      } catch (error) {
        if (userDataArray.length == 1 && response.failed_users) {
          failCsvDataToast(response.failed_users.error);
          return;
        }

        if (response && response.failed_users) {
          setFailedUsers(response.failed_users);
        }
        const errorMessage = TranslateErrorMessage(response.responseMessage);
        console.error(errorMessage);
        setError(errorMessage);

        const toastFunction =
          response.created_users.length <= 0
            ? uploadCsvDataToast
            : failCsvDataToast;

        const createdCount =
          response.created_users.length ||
          (response.created_users.length ? 1 : 0);
        const failedCount =
          response.failed_users.length ||
          (response.created_users.length ? 0 : 1);

        toastFunction(messages.user.uploaded(createdCount, failedCount, true));
      }
    };

    await postUserInformation(userDataArray);
  };

  const onCancel = () => {
    navigate(USER_PATHS.main);
  };

  const pollForStatus = async (
    requestId,
    totalUsers,
    poll_interval,
    timeout,
  ) => {
    const TIMEOUT = timeout * totalUsers;
    const startTime = Date.now();

    setIsProcessing(true);

    const poll = async () => {
      try {
        const statusResponse = await getUploadStatus(requestId, totalUsers);

        const { message, processed_count, total_users } = statusResponse.data;

        setProcessedCount(processed_count);

        if (
          message !== POLLING_MESSAGES.PROCESSING &&
          processed_count === total_users
        ) {
          setIsProcessing(false);
          processResult(statusResponse.data);
          return statusResponse.data;
        }

        if (Date.now() - startTime > TIMEOUT) {
          setIsProcessing(false);
          setError(error_message.csv.timeout);
          failCsvDataToast(error_message.csv.timeout);
          failCsvDataToast(error_message.csv.timeout_users_created);
          return;
        }

        setTimeout(poll, poll_interval);
      } catch (error) {
        if (error.status === 401) {
          setTimeout(poll, 5000);
          return;
        }

        setError(error_message.csv.polling_error);
        failCsvDataToast(error_message.csv.polling_error);
        setIsProcessing(false);
      }
    };

    poll();
  };

  const processResult = (finalResult) => {
    const numberOfUsers = csvData.length;

    if (finalResult.failed_users.length > 0) {
      const message =
        numberOfUsers == 1
          ? error_message.user.single_user_upload_failed
          : messages.user.uploaded(
              finalResult.created_users.length,
              finalResult.failed_users.length,
              true,
            );

      failCsvDataToast(message);
    } else {
      const message =
        numberOfUsers == 1
          ? messages.user.single_user_uploaded
          : messages.user.uploaded(
              finalResult.created_users.length,
              finalResult.failed_users.length,
              true,
            );

      uploadCsvDataToast(message);
    }

    finalResult.failed_users?.forEach((user) => {
      if (user.error.includes(csvNotFoundErrorMessage)) {
        if (user.method == METHOD_TYPE.DELETE) {
          const message = TranslateErrorMessage(deleteCsvNotFoundMessage);
          user.error = message;
          return;
        } else if (user.method == METHOD_TYPE.UPDATE) {
          const message = TranslateErrorMessage(updateCsvNotFoundMessage);
          user.error = message;
          return;
        }
      }

      const translatedMessage = TranslateErrorMessage(user.error);
      user.error = translatedMessage;
    });

    setFailedUsers(finalResult.failed_users);
  };

  return {
    loading,
    isProcessing,
    processedCount,
    csvData,
    failedUsers,
    currentDate,
    handleCsvUpload,
    setCsvData,
    onCancel,
  };
};
